import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaHeart, FaStar, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaCheck, FaShieldAlt, FaTag, FaHeadset } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import GlobalSearch from '../components/GlobalSearch';
import PopularCities from '../components/PopularCities';
import Footer from '../components/Footer';
import { getPropertyActivities } from '../utils/propertyActivities';
import { getPropertyImage, getImageUrl, FALLBACK_PROPERTY_IMAGE } from '../utils/imageHelper';
import { getApiUrl } from '../config/api';
import { formatPrice, getPreferredCurrency } from '../utils/currencyPreference';
import { getFavoriteId, loadWishlistFavorites, saveWishlistFavorites } from '../utils/wishlistStorage';
import useSmartMatch from '../hooks/useSmartMatch';
import { inferIntentFromSearchParams } from '../utils/smartMatchScore';
import { SmartMatchBadge } from '../components/SmartMatchBadge';
import { filterMeetingRoomsVisibleToUser } from '../utils/meetingRoomVisibility';
import { mergeUserWithStaffSession } from '../utils/companyStaffSession';
import OffersDealsShowcase, { resolveOfferPropertyId } from '../components/OffersDealsShowcase';
import TrustBadgesShowcase from '../components/TrustBadgesShowcase';
import { TrustBadgeRow } from '../components/TrustBadge';
import './HomePage.css';
import '../components/SmartMatchBadge.css';
import '../components/TrustBadge.css';

const homeHeroImageUrl = `${process.env.PUBLIC_URL || ''}/home-hero-content-room.png`;

const parseUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// ✅ SECURITY: Get user-specific last search key
const getLastSearchKey = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
      return `lastSearch_${userObj._id || userObj.id}`;
    }
  } catch (error) {
    console.error('Error getting user ID:', error);
  }
  return 'lastSearch'; // Fallback for non-logged-in users
};

const parseLastSearch = () => {
  try {
    const lastSearchKey = getLastSearchKey();
    const raw = localStorage.getItem(lastSearchKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const COMPANY_WELCOME_NAMES = {
  'abc-ltd': 'abc',
  'pqr-ltd': 'pqr',
  'xyz-ltd': 'xyz',
};

const COMPANY_EMAIL_HINTS = [
  { needle: '.abc', name: 'abc' },
  { needle: '.pqr', name: 'pqr' },
  { needle: '.xyz', name: 'xyz' },
];

const getCompanyWelcomeName = (user) => {
  if (!user) return '';
  const company = user.company;

  if (company && typeof company === 'object') {
    const slugName = COMPANY_WELCOME_NAMES[String(company.slug || '').toLowerCase()];
    if (slugName) return slugName;

    const companyName = company.profile?.displayName || company.name || '';
    const shortName = String(companyName).trim().split(/\s+/)[0];
    if (shortName) return shortName.toLowerCase();
  }

  const companySlug = typeof company === 'string' ? company : user.companySlug;
  const slugName = COMPANY_WELCOME_NAMES[String(companySlug || '').toLowerCase()];
  if (slugName) return slugName;

  const emailLocalPart = String(user.email || '').toLowerCase().split('@')[0] || '';
  const matchedHint = COMPANY_EMAIL_HINTS.find((hint) => emailLocalPart.includes(hint.needle));
  return matchedHint?.name || '';
};

const formatShortDate = (ymd) => {
  if (!ymd) return '';
  try {
    return new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return ymd;
  }
};

const sortOffersNewestFirst = (offers = []) => {
  const toTs = (offer) => {
    const raw =
      offer?.createdAt ||
      offer?.updatedAt ||
      offer?.discount?.createdAt ||
      offer?.discount?.updatedAt ||
      offer?.property?.createdAt ||
      offer?.property?.updatedAt;
    const ts = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(ts) ? ts : 0;
  };

  return [...offers].sort((a, b) => toTs(b) - toTs(a));
};

const mapMeetingRoomToRecommendation = (room) => {
  const roomId = room?._id || room?.id;
  const city = room?.city || room?.location || room?.address || 'Location';
  const capacity = Number(room?.capacity || 0);
  const amenities = Array.isArray(room?.amenities) ? room.amenities.filter(Boolean) : [];
  const descriptionParts = [
    `Meeting Room in ${city}`,
    capacity > 0 ? `${capacity} seats` : null,
    amenities.length > 0 ? amenities.slice(0, 2).join(' • ') : null,
  ].filter(Boolean);

  return {
    id: roomId,
    propertyId: null,
    meetingRoomId: roomId,
    listingKind: 'meeting_room',
    title: room?.name || 'Meeting Room',
    description: descriptionParts.join(' • '),
    location: city,
    pricePerNight: Number(room?.pricePerHour || 0),
    image: Array.isArray(room?.images) && room.images.length > 0 ? room.images[0] : '',
    images: Array.isArray(room?.images) ? room.images : [],
    rating: 0,
    reviews: 0,
    type: room?.roomType || 'Meeting Room',
    keywords: Array.isArray(room?.keywords) ? room.keywords : [],
    rules: room?.rules || '',
    hostEmail: room?.hostId?.email || room?.host?.email || room?.ownerEmail || '',
    createdAt: room?.createdAt,
  };
};


const HomePage = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const user = useMemo(() => parseUser(), []);
  const lastSearch = useMemo(() => parseLastSearch(), []);

  // ✅ SECURITY: Get user-specific favorites key
  const getFavoritesKey = () => {
    try {
      if (user) {
        return `favorites_${user._id || user.id}`;
      }
    } catch (error) {
      console.error('Error getting user ID:', error);
    }
    return 'favorites'; // Fallback for non-logged-in users
  };

  // ✅ SECURITY: Get user-specific wishlist count key
  const getWishlistCountKey = () => {
    try {
      if (user) {
        return `wishlistCount_${user._id || user.id}`;
      }
    } catch (error) {
      console.error('Error getting user ID:', error);
    }
    return 'wishlistCount'; // Fallback for non-logged-in users
  };

  // Load favorites on mount
  useEffect(() => {
    try {
      const favoritesKey = getFavoritesKey();
      const favs = loadWishlistFavorites(favoritesKey);
      const favoriteIds = new Set(favs.map(fav => getFavoriteId(fav)).filter(Boolean));
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  }, [user]);

  // Toggle favorite handler
  const handleToggleFavorite = (e, property) => {
    e.stopPropagation();
    
    try {
      const favoritesKey = getFavoritesKey();
      const wishlistCountKey = getWishlistCountKey();
      let favoritesList = loadWishlistFavorites(favoritesKey);
      
      const propertyId = property.propertyId || property.id || property._id;
      
      if (!propertyId) {
        console.error('No property ID found');
        return;
      }

      // Prepare property data for storage
      const propertyData = {
        id: propertyId,
        _id: propertyId,
        propertyId: propertyId,
        name: property.title || property.name,
        title: property.title || property.name,
        city: property.location || property.city,
        location: property.location || property.city,
        rating: property.rating,
        reviews: property.reviews,
        pricePerNight: property.pricePerNight,
        images: property.image ? [property.image] : [],
        image: property.image,
        type: property.type,
        ...property
      };

      const isCurrentlyFavorite = favorites.has(propertyId);

      if (isCurrentlyFavorite) {
        // Remove from favorites
        favoritesList = favoritesList.filter(fav => getFavoriteId(fav) !== propertyId);
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(propertyId);
          return newSet;
        });
      } else {
        // Add to favorites
        favoritesList = favoritesList.filter(fav => getFavoriteId(fav) !== propertyId);
        favoritesList.push({
          ...propertyData,
          wishlistAddedAt: Date.now()
        });
        setFavorites(prev => new Set(prev).add(propertyId));
      }

      saveWishlistFavorites(favoritesKey, wishlistCountKey, favoritesList);
      
      // Trigger storage event to update navbar
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const firstName = useMemo(() => {
    const companyWelcomeName = getCompanyWelcomeName(user);
    if (companyWelcomeName) return companyWelcomeName;
    const name = user?.name || user?.email || 'Guest';
    return String(name).trim().split(' ')[0] || 'Guest';
  }, [user]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load real recent activities from storage - ONLY SHOW LAST ACTIVITY
  useEffect(() => {
    const loadActivities = () => {
      try {
        const activities = getPropertyActivities();
        
        // Get only the most recent (last) activity
        const lastActivity = activities.length > 0 ? activities[0] : null;
        
        if (lastActivity) {
          // Transform activity to match the expected format
          const formattedActivity = {
            id: lastActivity.id,
            propertyId: lastActivity.propertyId,
            title: lastActivity.title,
            location: lastActivity.location,
            rating: lastActivity.rating,
            nights: lastActivity.nights,
            pricePerNight: lastActivity.pricePerNight,
            currency: lastActivity.currency || 'USD',
            image: lastActivity.image || 'https://images.unsplash.com/photo-1501117716987-c8e1ecb2101d?w=1200&q=80',
            checkIn: lastActivity.checkIn,
            checkOut: lastActivity.checkOut,
            source: lastActivity.source || '',
            link: lastActivity.link || '',
            timestamp: lastActivity.timestamp
          };
          
          setRecentActivity([formattedActivity]);
        } else {
          setRecentActivity([]);
        }
      } catch (error) {
        console.error('Error loading activities:', error);
        setRecentActivity([]);
      }
    };

    // Load initial activities
    loadActivities();

    // Listen for real-time updates
    const handleActivityUpdate = (event) => {
      const activities = event.detail || [];
      
      // Get only the most recent (last) activity
      const lastActivity = activities.length > 0 ? activities[0] : null;
      
      if (lastActivity) {
        const formattedActivity = {
          id: lastActivity.id,
          propertyId: lastActivity.propertyId,
          title: lastActivity.title,
          location: lastActivity.location,
          rating: lastActivity.rating,
          nights: lastActivity.nights,
          pricePerNight: lastActivity.pricePerNight,
          currency: lastActivity.currency || 'USD',
          image: lastActivity.image || 'https://images.unsplash.com/photo-1501117716987-c8e1ecb2101d?w=1200&q=80',
          checkIn: lastActivity.checkIn,
          checkOut: lastActivity.checkOut,
          source: lastActivity.source || '',
          link: lastActivity.link || '',
          timestamp: lastActivity.timestamp
        };
        setRecentActivity([formattedActivity]);
      } else {
        setRecentActivity([]);
      }
    };

    window.addEventListener('propertyActivityUpdated', handleActivityUpdate);

    // Also listen for storage changes (in case of multiple tabs)
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('propertyActivities')) {
        loadActivities();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('propertyActivityUpdated', handleActivityUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);


  // Fallback data when API fails or returns empty
  const defaultOffers = [
    { id: 'offer-1', badge: 'Limited Time Offer', title: 'Business Stay Offer', description: 'Perfect stay for business travelers.', details: 'Early check-in • 15% off', pricePerNight: 1720, originalPrice: 2220, image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&q=80' },
    { id: 'offer-2', badge: 'Early Bird Special', title: 'Early Bird Special', description: 'Book early and enjoy exclusive savings.', details: 'Late check-out • 20% off', pricePerNight: 1989, originalPrice: 2340, image: 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=1200&q=80' },
    { id: 'offer-3', badge: 'Weekend Getaway', title: 'Weekend Escape', description: 'Relax with a special weekend discount.', details: 'Pool access • 18% off', pricePerNight: 2450, originalPrice: 2990, image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80' },
    { id: 'offer-4', badge: 'Member Deal', title: 'Luxury Suite Offer', description: 'Premium suite at an unbeatable price.', details: 'Free breakfast • 25% off', pricePerNight: 3890, originalPrice: 5190, image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=1200&q=80' },
  ];
  const [allOffers, setAllOffers] = useState(defaultOffers);
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(true);

  const homeMatchIntent = useMemo(
    () =>
      inferIntentFromSearchParams({
        location: lastSearch?.location || lastSearch?.city || '',
        checkIn: lastSearch?.checkIn,
        checkOut: lastSearch?.checkOut,
        guests: lastSearch?.guests,
        maxPrice: lastSearch?.priceRange?.[1],
      }),
    [lastSearch]
  );

  const stayRecommendations = useMemo(
    () =>
      recommended
        .filter((item) => item.listingKind !== 'meeting_room')
        .map((item) => ({
          ...item,
          _id: item.propertyId || item.id,
          name: item.title,
          city: item.location,
        })),
    [recommended]
  );

  const { scoredProperties: scoredRecommendations } = useSmartMatch(stayRecommendations, homeMatchIntent);

  const recommendationMatchById = useMemo(() => {
    const map = new Map();
    scoredRecommendations.forEach((item) => {
      map.set(String(item._id || item.id), item.smartMatch);
    });
    return map;
  }, [scoredRecommendations]);

  // Fetch real property offers (section shows first 2; full list on offers page)
  useEffect(() => {
    const fetchPropertyOffers = async () => {
      try {
        setLoadingOffers(true);
        const response = await fetch(getApiUrl('/api/properties/offers-with-discounts'));
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setAllOffers(defaultOffers);
        } else {
          const result = await response.json();
          if (result.success && result.offers && result.offers.length > 0) {
            setAllOffers(sortOffersNewestFirst(result.offers));
          }
        }
      } catch (error) {
        console.error('Error fetching property offers:', error);
        setAllOffers(defaultOffers);
      } finally {
        setLoadingOffers(false);
      }
    };

    fetchPropertyOffers();
  }, []);
  // Fetch recommended listings (prefer host properties; fallback to owner-added meeting rooms)
  useEffect(() => {
    const fetchRecommendedListings = async () => {
      try {
        setLoadingRecommended(true);
        const token = localStorage.getItem('token');
        const headers = token
          ? { Accept: 'application/json', Authorization: `Bearer ${token}` }
          : { Accept: 'application/json' };

        const response = await fetch(getApiUrl('/api/properties/recommended-villas'), { headers });
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const result = await response.json();
          if (result.success && result.recommendations && result.recommendations.length > 0) {
            setRecommended(result.recommendations.slice(0, 6));
            return;
          }
        }

        // If no stay properties, show owner/company-added meeting rooms.
        const roomsResponse = await fetch(
          getApiUrl('/api/meetings/rooms?ownerAddedOnly=true&managedOnly=true&sort=latest'),
          { headers }
        );
        const roomsContentType = roomsResponse.headers.get('content-type');
        if (roomsContentType && roomsContentType.includes('application/json')) {
          const rooms = await roomsResponse.json();
          const visibleRooms = filterMeetingRoomsVisibleToUser(
            Array.isArray(rooms) ? rooms : [],
            mergeUserWithStaffSession(parseUser())
          );
          if (visibleRooms.length > 0) {
            setRecommended(visibleRooms.slice(0, 6).map(mapMeetingRoomToRecommendation));
            return;
          }
        }

        setRecommended([]);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setRecommended([]);
      } finally {
        setLoadingRecommended(false);
      }
    };

    fetchRecommendedListings();
  }, []);

  return (
    <div className="home-page">
      <Navbar isScrolled={isScrolled} />

      <div className="home-page-main">
      <section className="home-hero">
        <div
          className="home-hero-bg"
          style={{
            backgroundImage: `url(${homeHeroImageUrl})`,
          }}
        />
        <div className="home-hero-overlay" />
        <div className="home-hero-content">
          <motion.h1
            className="home-hero-title"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Welcome back, {firstName}
          </motion.h1>
          <motion.p
            className="home-hero-subtitle"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            Find your next perfect stay
          </motion.p>

          <motion.div
            className="home-hero-pill"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="pill-dot" />
            <span className="pill-text">
              Continue your last search:{' '}
              <strong>{lastSearch?.location || 'Mangalore'}</strong>
              {lastSearch?.checkIn && lastSearch?.checkOut
                ? ` • ${formatShortDate(lastSearch.checkIn)} - ${formatShortDate(
                    lastSearch.checkOut
                  )}`
                : ' • Select dates'}
              {' • '}
              {lastSearch?.guests?.adults || 2} Adults •{' '}
              {lastSearch?.guests?.rooms || 1} Room
            </span>
          </motion.div>
        </div>
      </section>

      <div className="home-global-search">
        <GlobalSearch />
      </div>

      <main className="home-main">
        <section className="home-section">
          <div className="home-section-header">
            <h2 className="home-section-title">Recent Activity</h2>
            <p className="home-section-subtitle">Recently viewed & saved places</p>
          </div>

          <div className="home-recent-grid">
            {recentActivity.length > 0 ? (
              recentActivity.map((item) => (
                <motion.article
                  key={item.id}
                  className="recent-card"
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => {
                    if (item.source === 'serpapi' && item.link) {
                      window.open(item.link, '_blank', 'noopener,noreferrer');
                    } else {
                      const url = `/property/${item.propertyId}${item.checkIn && item.checkOut ? `?checkIn=${item.checkIn}&checkOut=${item.checkOut}` : ''}`;
                      navigate(url);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="recent-image">
                    <img
                      className="recent-image-photo"
                      src={getImageUrl(item.image)}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const el = e.currentTarget;
                        el.onerror = null;
                        el.src = FALLBACK_PROPERTY_IMAGE;
                      }}
                    />
                  </div>
                  <div className="recent-content">
                    <div className="recent-top">
                      <div>
                        <h3 className="card-title">{String(item.title || 'Property')}</h3>
                        <p className="card-subtitle">{String(item.location || 'Location')}</p>
                      </div>
                      <button 
                        className="icon-like" 
                        type="button" 
                        aria-label="Save"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle favorite toggle
                        }}
                      >
                        <FaHeart />
                      </button>
                    </div>

                    <div className="recent-meta">
                      {item.rating > 0 && (
                        <span className="rating">
                          <FaStar /> {String(item.rating)}
                        </span>
                      )}
                      <span className="muted">
                        {formatPrice(item.pricePerNight || 0, item.currency || getPreferredCurrency())} / night
                      </span>
                    </div>

                    <div className="recent-footer">
                      <span className="muted">
                        {item.checkIn && item.checkOut && item.nights
                          ? `${formatShortDate(item.checkIn)} — ${formatShortDate(item.checkOut)} • ${String(item.nights)} ${item.nights === 1 ? 'night' : 'nights'}`
                          : item.nights
                          ? `${String(item.nights)} ${item.nights === 1 ? 'night' : 'nights'}`
                          : 'Select dates'}
                      </span>
                      <button 
                        className="btn-primary-compact" 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.source === 'serpapi' && item.link) {
                            window.open(item.link, '_blank', 'noopener,noreferrer');
                          } else {
                            const url = `/property/${item.propertyId}${item.checkIn && item.checkOut ? `?checkIn=${item.checkIn}&checkOut=${item.checkOut}` : ''}`;
                            navigate(url);
                          }
                        }}
                      >
                        {formatPrice(item.pricePerNight || 0)} / night <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))
            ) : (
              <div className="recent-empty">
                <p className="muted">No recent activity yet. Start exploring properties!</p>
              </div>
            )}
          </div>
        </section>

        <section className="home-section">
          <div className="home-section-header split">
            <h2 className="home-section-title">Recommended For You</h2>
            <p className="home-section-subtitle">
              {user
                ? 'Personalised picks based on your preferences and booking history'
                : 'Latest owner-added stays & rooms'}
            </p>
          </div>

          {loadingRecommended ? (
            <div className="loading-state">
              <p>Loading recommendations...</p>
            </div>
          ) : recommended.length === 0   ? (
            <div className="empty-state">
              <p>No recommendations available at the moment.</p>
            </div>
          ) : (
            <div className="cards-row">
              {recommended.map((item) => {
                // Ensure we always have a valid image URL
                const imageUrl = getPropertyImage(item, 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600&q=80');
                const isMeetingRoom = item.listingKind === 'meeting_room';
                
                return (
                <motion.article
                  key={item.id}
                  className="mini-card recommended-card"
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mini-image">
                    <img
                      className="mini-image-photo"
                      src={imageUrl}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const el = e.currentTarget;
                        el.onerror = null;
                        el.src = FALLBACK_PROPERTY_IMAGE;
                      }}
                    />
                    <div className="property-type-badge">{item.type}</div>
                    {!isMeetingRoom && (
                      <button 
                        className={`icon-like ${favorites.has(item.propertyId || item.id) ? 'active' : ''}`}
                        type="button" 
                        aria-label={favorites.has(item.propertyId || item.id) ? 'Remove from favorites' : 'Add to favorites'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(e, item);
                        }}
                      >
                        <FaHeart />
                      </button>
                    )}
                  </div>
                  <div className="mini-content">
                    {!isMeetingRoom && recommendationMatchById.get(String(item.propertyId || item.id)) && (
                      <SmartMatchBadge
                        match={recommendationMatchById.get(String(item.propertyId || item.id))}
                        compact
                        showReasons={false}
                      />
                    )}
                    {!isMeetingRoom && <TrustBadgeRow property={item} limit={2} compact />}
                    <h3 className="card-title">{item.title}</h3>
                    <p className="card-subtitle">{item.description}</p>
                    <div className="mini-footer">
                      <div className="rating-section">
                        {item.rating > 0 && (
                          <span className="rating">
                            <FaStar /> {item.rating.toFixed(1)}
                          </span>
                        )}
                        {item.reviews > 0 && (
                          <span className="reviews">({item.reviews})</span>
                        )}
                      </div>
                      <span className="price">{formatPrice(item.pricePerNight)}</span>
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn-view-property"
                        onClick={() => {
                          if (isMeetingRoom && item.meetingRoomId) {
                            navigate(`/meetings/room/${item.meetingRoomId}`);
                          } else if (item.propertyId || item.id) {
                            navigate(`/property/${item.propertyId || item.id}`);
                          }
                        }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </motion.article>
                );
              })}
            </div>
          )}
          
          {/* View More Button */}
          {!loadingRecommended && recommended.length > 0 && (
            <motion.div
              className="view-more-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <button
                className="btn-view-more"
                onClick={() => navigate('/properties')}
              >
                View More Accommodations
                <FaArrowRight style={{ marginLeft: '8px' }} />
              </button>
            </motion.div>
          )}
        </section>

        <TrustBadgesShowcase />

        <PopularCities />

        <section className="home-section offers-deals-home-section">
          <OffersDealsShowcase
            offers={allOffers}
            loading={loadingOffers}
            carousel
            cardsPerSlide={2}
            showViewAll={allOffers.length > 0}
            onViewAll={() => navigate('/offers-deals')}
            onOfferClick={(item) => {
              const propertyId = resolveOfferPropertyId(item);
              if (propertyId) navigate(`/property/${propertyId}`);
            }}
          />
        </section>
      </main>

      <Footer />
      </div>
    </div>
  );
};

export default HomePage;
